<script>
import wangEditor from "wangeditor";
import moment from "moment";
import {
  EnterpriseStatusEnum,
  enterpriseStatusList,
  findLabel,
  downloadBlob,
} from "../help";
import {
  getEnterpriseList,
  addEnterprise,
  updateEnterprise,
  deleteEnterprise,
  downloadEnterpriseQr,
  downloadEnterpriseQrBatch,
  getExhibitorIntentions,
  exportExhibitorIntentions,
  getExhibitorLocations,
} from "@/api/techInnoActivities";
import { IMG_API } from "@/config/index";

const emptyForm = () => ({
  // id（编辑时使用）
  id: "",
  // 企业名称
  name: "",
  // 企业 logo（a-upload 文件列表，最多 1 张，提交取 logoUrl）
  logoFileList: [],
  // 展商地点（locationId，非必填，选项来自 7.10 地点字典）
  locationId: undefined,
  // 企业简介
  introduction: "",
  // 企业信息（富文本，wangeditor onchange 同步）
  content: "",
  // 排序（默认 0）
  sort: 0,
});

export default {
  name: "TechInnoEnterprises",
  data() {
    return {
      EnterpriseStatusEnum,
      enterpriseStatusList,
      IMG_API,
      // 展商地点字典（7.10）
      locationOptions: [],
      // 搜索条件
      searchForm: {
        // 企业名称，模糊
        name: "",
        // 状态（不选则查全部）
        status: "",
      },
      columns: [
        { title: "企业logo", key: "logoUrl", width: 100, scopedSlots: { customRender: "logo" } },
        { title: "企业名称", dataIndex: "name", key: "name", width: 200, ellipsis: true },
        {
          title: "企业简介",
          dataIndex: "introduction",
          key: "introduction",
          ellipsis: true,
          customRender: (text) => text || "--",
        },
        {
          title: "地点",
          dataIndex: "locationName",
          key: "locationName",
          width: 110,
          ellipsis: true,
          customRender: (text) => text || "--",
        },
        {
          title: "浏览次数",
          dataIndex: "viewCount",
          key: "viewCount",
          width: 90,
          customRender: (text) => (text != null ? text : "--"),
        },
        {
          title: "扫码次数",
          dataIndex: "scanCount",
          key: "scanCount",
          width: 90,
          customRender: (text) => (text != null ? text : "--"),
        },
        { title: "排序", dataIndex: "sort", key: "sort", width: 80 },
        {
          title: "状态",
          key: "status",
          width: 90,
          scopedSlots: { customRender: "status" },
        },
        { title: "创建时间", dataIndex: "createTime", key: "createTime", width: 170, ellipsis: true },
        { title: "操作", key: "action", width: 395, fixed: "right", scopedSlots: { customRender: "action" } },
      ],
      dataInfo: [],
      listLoading: false,
      // 新增/编辑弹窗
      visibleModal: false,
      // 操作类型 add 新增 / edit 编辑
      operationType: "add",
      // 编辑时沿用原上下架状态
      editingStatus: null,
      form: emptyForm(),
      confirmLoading: false,
      // 富文本实例
      editor: null,
      rules: {
        name: [
          { required: true, message: "请输入企业名称", trigger: "blur" },
          { max: 20, message: "企业名称不超过 20 字", trigger: "blur" },
        ],
        locationId: [{ required: true, message: "请选择地点", trigger: "change" }],
        logoFileList: [
          { required: true, type: "array", message: "请上传企业logo", trigger: "change" },
        ],
        introduction: [{ max: 200, message: "企业简介不超过 200 字", trigger: "blur" }],
      },
      // logo 预览
      previewVisible: false,
      previewUrl: "",
      // 查看弹窗
      viewVisible: false,
      viewData: {},
      // 合作意向登记弹窗（7.8 按企业分页查询）
      intentionVisible: false,
      intentionRecord: {},
      intentionList: [],
      intentionLoading: false,
      // 意向导出 loading
      intentionExportLoading: false,
      intentionColumns: [
        { title: "机构", dataIndex: "organization", key: "organization", width: 150, ellipsis: true, customRender: (text) => text || "--" },
        { title: "职务", dataIndex: "jobTitle", key: "jobTitle", width: 110, ellipsis: true, customRender: (text) => text || "--" },
        { title: "姓名", dataIndex: "contactName", key: "contactName", width: 110, ellipsis: true, customRender: (text) => text || "--" },
        { title: "联系电话", dataIndex: "contactPhone", key: "contactPhone", width: 130, ellipsis: true, customRender: (text) => text || "--" },
        { title: "合作方向", dataIndex: "cooperationDirection", key: "cooperationDirection", ellipsis: true, customRender: (text) => text || "--" },
        { title: "账号手机号", dataIndex: "accountPhone", key: "accountPhone", width: 130, ellipsis: true, customRender: (text) => text || "--" },
        { title: "填写时间", dataIndex: "createTime", key: "createTime", width: 170 },
      ],
      intentionPagination: {
        pageSizeOptions: ["10", "20", "50"],
        showSizeChanger: true,
        current: 1,
        pageSize: 10,
        total: 0,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
      },
      // 二维码下载 loading（行级；批量共用一个）
      qrLoadingId: null,
      batchQrLoading: false,
      pagination: {
        pageSizeOptions: ["10", "20", "30", "50", "100"],
        showSizeChanger: true,
        current: 1,
        pageSize: 10,
        total: 0,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
      },
    };
  },
  created() {
    this.getLocationOptions();
    this.getList();
  },
  mounted() {
    // 宽屏下表格无横向滚动，取消操作列固定，避免固定列 overlay 产生空白列
    this.handleResize = () => {
      const wide = this.$el.clientWidth >= 1585;
      const cols = this.columns;
      cols[cols.length - 1].fixed = wide ? "" : "right";
    };
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.destroyEditor();
  },
  methods: {
    // 模板中使用的字典取值函数需挂在实例上
    findLabel,
    /**
     * @Description：展商地点字典（7.10）
     * @Author cxd
     * @Date 2026/11/5
     */
    async getLocationOptions() {
      try {
        const res = await getExhibitorLocations();
        if (String(res.code) === "200") {
          this.locationOptions = Array.isArray(res.data) ? res.data : [];
        }
      } catch (e) {
        this.locationOptions = [];
      }
    },
    /**
     * @Description：展商企业列表（接口 page 从 1 开始）
     * @Author cxd
     * @Date 2026/11/1
     */
    async getList() {
      const params = {
        name: this.searchForm.name,
        status: this.searchForm.status === "" ? undefined : this.searchForm.status,
        page: this.pagination.current,
        size: this.pagination.pageSize,
      };
      this.listLoading = true;
      try {
        const res = await getEnterpriseList(params);
        if (String(res.code) === "200") {
          this.dataInfo = (res.data && res.data.items) || [];
          this.pagination.total = (res.data && res.data.total) || 0;
        } else {
          this.$message.error(res.msg || "查询失败");
        }
      } catch (e) {
        this.$message.error("查询失败");
      } finally {
        this.listLoading = false;
      }
    },
    /**
     * @Description：查询
     * @Author cxd
     * @Date 2026/11/1
     */
    search() {
      this.pagination.current = 1;
      this.getList();
    },
    /**
     * @Description：重置
     * @Author cxd
     * @Date 2026/11/1
     */
    resetSearchForm() {
      this.searchForm = { name: "", status: "" };
      this.pagination.current = 1;
      this.pagination.pageSize = 10;
      this.getList();
    },
    /**
     * @Description：分页
     * @Author cxd
     * @Date 2026/11/1
     */
    pageChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getList();
    },
    /**
     * @Description：新增
     * @Author cxd
     * @Date 2026/11/1
     */
    handleAdd() {
      this.operationType = "add";
      this.editingStatus = null;
      this.form = emptyForm();
      this.visibleModal = true;
      this.$nextTick(() => this.createEditor(""));
    },
    /**
     * @Description：编辑（列表行返回字段同详情，直接回显）
     * @Author cxd
     * @Date 2026/11/1
     */
    handleEdit(record) {
      this.operationType = "edit";
      this.editingStatus = record.status != null ? record.status : EnterpriseStatusEnum.ON_SHELF;
      this.form = emptyForm();
      this.form.id = record.id;
      this.form.name = record.name || "";
      this.form.introduction = record.introduction || "";
      this.form.locationId = record.locationId != null ? record.locationId : undefined;
      this.form.content = record.content || "";
      this.form.sort = record.sort != null ? Number(record.sort) : 0;
      this.form.logoFileList = record.logoUrl
        ? [{ uid: "1", name: "logo.png", status: "done", url: record.logoUrl }]
        : [];
      this.visibleModal = true;
      this.$nextTick(() => this.createEditor(this.form.content));
    },
    /**
     * @Description：创建富文本编辑器（wangeditor，图片走 OSS 上传）
     * @Author cxd
     * @Date 2026/11/1
     */
    createEditor(html) {
      this.destroyEditor();
      const editor = new wangEditor("#enterpriseEditor");
      editor.config.height = 260;
      editor.config.showFullScreen = false;
      editor.config.menus = [
        "fontSize",
        "foreColor",
        "backColor",
        "underline",
        "italic",
        "bold",
        "justify",
        "splitLine",
        "undo",
        "redo",
        "list",
        "table",
        "image",
      ];
      editor.config.uploadImgServer = IMG_API + "/oss/files";
      editor.config.uploadImgShowBase64 = false;
      editor.config.showLinkImg = false;
      editor.config.uploadImgMaxSize = 10 * 1024 * 1024;
      editor.config.uploadImgAccept = ["jpg", "jpeg", "png", "gif"];
      editor.config.uploadImgMaxLength = 1;
      editor.config.uploadFileName = "file";
      editor.config.uploadImgTimeout = 60000;
      // OSS 返回 {redirect_uri}，非 wangeditor 默认格式，需 customInsert 手动插入
      editor.config.uploadImgHooks = {
        fail: () => this.$message.error("图片上传失败"),
        error: () => this.$message.error("上传请求失败"),
        timeout: () => this.$message.error("上传请求超时"),
        customInsert: (insertImgFn, result) => {
          insertImgFn(result.redirect_uri);
        },
      };
      editor.config.onchange = (newHtml) => {
        this.form.content = newHtml;
      };
      editor.create();
      if (html) {
        editor.txt.html(html);
      }
      this.editor = editor;
    },
    /**
     * @Description：销毁富文本编辑器
     * @Author cxd
     * @Date 2026/11/1
     */
    destroyEditor() {
      if (this.editor) {
        try {
          this.editor.destroy();
        } catch (e) {
          // 容器可能已随弹窗销毁
        }
        this.editor = null;
      }
    },
    /**
     * @Description：logo 上传前校验（仅 jpg/png，比例须 1:1，大小不限）
     * @Author cxd
     * @Date 2026/11/3
     */
    logoBeforeUpload(file) {
      const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
      if (!isImage) {
        this.$message.error("仅支持 jpg/png 格式图片");
        return false;
      }
      // 读取图片校验宽高比例 1:1，通过后 resolve 才继续上传
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            if (img.width !== img.height) {
              this.$message.error("logo 须为 1:1 正方形图片，请调整后重新上传");
              reject();
              return;
            }
            resolve();
          };
          img.onerror = () => {
            this.$message.error("图片读取失败，请重新选择");
            reject();
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    },
    /**
     * @Description：logo 上传变化（最多保留 1 张）
     * @Author cxd
     * @Date 2026/11/1
     */
    handleLogoChange({ fileList }) {
      if (fileList.length > 1) {
        fileList = fileList.slice(-1);
      }
      this.form.logoFileList = fileList;
    },
    /**
     * @Description：logo 预览
     * @Author cxd
     * @Date 2026/11/1
     */
    handlePreview(file) {
      this.previewUrl = file.url || (file.response && file.response.redirect_uri);
      this.previewVisible = true;
    },
    /**
     * @Description：取 logo 地址（新上传取 response.redirect_uri，回显取 url）
     * @Author cxd
     * @Date 2026/11/1
     */
    getLogoUrl() {
      const file = this.form.logoFileList[0];
      if (!file) return "";
      return file.response ? file.response.redirect_uri : file.url || "";
    },
    /**
     * @Description：弹窗确认提交（新增默认上架；编辑沿用原状态；字段与 7.1 对齐）
     * @Author cxd
     * @Date 2026/11/1
     */
    handleOk() {
      this.$refs.ruleForm.validate(async (valid) => {
        if (!valid) return;
        this.confirmLoading = true;
        try {
          const params = {
            id: this.form.id,
            name: this.form.name,
            logoUrl: this.getLogoUrl(),
            locationId: this.form.locationId,
            introduction: this.form.introduction,
            content: this.form.content,
            sort: this.form.sort != null ? this.form.sort : 0,
            status:
              this.operationType === "edit" && this.editingStatus != null
                ? this.editingStatus
                : EnterpriseStatusEnum.ON_SHELF,
          };
          const res =
            this.operationType === "edit"
              ? await updateEnterprise(params)
              : await addEnterprise(params);
          if (String(res.code) === "200") {
            this.$message.success(res.msg || "保存成功");
            this.visibleModal = false;
            this.getList();
          } else {
            this.$message.error(res.msg || "保存失败");
          }
        } catch (e) {
          this.$message.error("保存失败");
        } finally {
          this.confirmLoading = false;
        }
      });
    },
    /**
     * @Description：查看（含企业信息富文本；二维码改为操作栏「下载二维码」走服务端下载）
     * @Author cxd
     * @Date 2026/11/3
     */
    handleView(record) {
      this.viewData = { ...record };
      this.viewVisible = true;
    },
    /**
     * @Description：下载单个企业二维码（7.6，PNG 流，服务端文件名=企业名称.png）
     * @Author cxd
     * @Date 2026/11/3
     */
    async handleDownloadQr(record) {
      this.qrLoadingId = record.id;
      try {
        const res = await downloadEnterpriseQr(record.id);
        downloadBlob(res, `${record.name}.png`);
      } catch (e) {
        this.$message.error("二维码下载失败，请稍后重试");
      } finally {
        this.qrLoadingId = null;
      }
    },
    /**
     * @Description：批量下载企业二维码（7.7，ZIP 流，按当前筛选条件导出）
     * @Author cxd
     * @Date 2026/11/3
     */
    async handleBatchQr() {
      this.batchQrLoading = true;
      try {
        const res = await downloadEnterpriseQrBatch({
          name: this.searchForm.name,
          status: this.searchForm.status === "" ? undefined : this.searchForm.status,
        });
        downloadBlob(res, `企业二维码-${moment().format("YYYYMMDDHHmmss")}.zip`);
      } catch (e) {
        this.$message.error("批量下载失败，请稍后重试");
      } finally {
        this.batchQrLoading = false;
      }
    },
    /**
     * @Description：合作意向登记（弹窗按企业分页加载意向记录，7.8）
     * @Author cxd
     * @Date 2026/11/3
     */
    handleIntention(record) {
      this.intentionRecord = record;
      this.intentionVisible = true;
      this.intentionPagination.current = 1;
      this.getIntentionList();
    },
    /**
     * @Description：合作意向列表（接口 page 从 1 开始）
     * @Author cxd
     * @Date 2026/11/3
     */
    async getIntentionList() {
      if (!this.intentionRecord.id) return;
      this.intentionLoading = true;
      try {
        const res = await getExhibitorIntentions(this.intentionRecord.id, {
          page: this.intentionPagination.current,
          size: this.intentionPagination.pageSize,
        });
        if (String(res.code) === "200") {
          this.intentionList = (res.data && res.data.items) || [];
          this.intentionPagination.total = (res.data && res.data.total) || 0;
        } else {
          this.$message.error(res.msg || "查询失败");
        }
      } catch (e) {
        this.$message.error("查询失败");
      } finally {
        this.intentionLoading = false;
      }
    },
    /**
     * @Description：导出该企业合作意向 Excel（7.9，导出该企业全部意向）
     * @Author cxd
     * @Date 2026/11/3
     */
    async handleExportIntentions() {
      if (!this.intentionRecord.id) return;
      this.intentionExportLoading = true;
      try {
        const res = await exportExhibitorIntentions(this.intentionRecord.id);
        downloadBlob(res, `${this.intentionRecord.name}_合作意向.xlsx`);
      } catch (e) {
        this.$message.error("导出失败，请稍后重试");
      } finally {
        this.intentionExportLoading = false;
      }
    },
    /**
     * @Description：合作意向分页
     * @Author cxd
     * @Date 2026/11/3
     */
    intentionPageChange(pagination) {
      this.intentionPagination.current = pagination.current;
      this.intentionPagination.pageSize = pagination.pageSize;
      this.getIntentionList();
    },
    /**
     * @Description：删除（二次确认）
     * @Author cxd
     * @Date 2026/11/1
     */
    handleDelete(record) {
      const that = this;
      this.$confirm({
        title: "确认删除该企业？",
        content: `删除后不可恢复：${record.name}`,
        okText: "确认",
        cancelText: "取消",
        async onOk() {
          try {
            const res = await deleteEnterprise(record.id);
            if (String(res.code) === "200") {
              that.$message.success(res.msg || "删除成功");
              that.getList();
            } else {
              that.$message.error(res.msg || "删除失败");
            }
          } catch (e) {
            that.$message.error("删除失败");
          }
        },
        onCancel() {},
      });
    },
    /**
     * @Description：上下架提交体（仅 7.1 字段；不能整行透传，qrCodeContent/createTime 等只读字段会导致后端报错）
     * @Author cxd
     * @Date 2026/11/1
     */
    buildShelfPayload(record, status) {
      return {
        id: record.id,
        name: record.name,
        logoUrl: record.logoUrl,
        locationId: record.locationId != null ? record.locationId : undefined,
        introduction: record.introduction,
        content: record.content,
        sort: record.sort,
        status,
      };
    },
    /**
     * @Description：上架（直接生效，全量 PUT 改 status）
     * @Author cxd
     * @Date 2026/11/1
     */
    async handleOnShelf(record) {
      try {
        const res = await updateEnterprise(
          this.buildShelfPayload(record, EnterpriseStatusEnum.ON_SHELF)
        );
        if (String(res.code) === "200") {
          this.$message.success(res.msg || "已上架");
          this.getList();
        } else {
          this.$message.error(res.msg || "操作失败");
        }
      } catch (e) {
        this.$message.error("操作失败");
      }
    },
    /**
     * @Description：下架（二次确认，全量 PUT 改 status）
     * @Author cxd
     * @Date 2026/11/1
     */
    handleOffShelf(record) {
      const that = this;
      this.$confirm({
        title: "确认下架该企业？",
        content: `下架后用户端不再展示：${record.name}`,
        okText: "确认",
        cancelText: "取消",
        async onOk() {
          try {
            const res = await updateEnterprise(
              that.buildShelfPayload(record, EnterpriseStatusEnum.OFF_SHELF)
            );
            if (String(res.code) === "200") {
              that.$message.success(res.msg || "已下架");
              that.getList();
            } else {
              that.$message.error(res.msg || "操作失败");
            }
          } catch (e) {
            that.$message.error("操作失败");
          }
        },
        onCancel() {},
      });
    },
  },
};
</script>

<template>
  <div class="tech-inno-enterprises">
    <ds-header title="科创企业管理"></ds-header>
    <a-form layout="inline" style="margin-bottom: 10px">
      <a-form-item label="企业名称：">
        <a-input
          v-model.trim="searchForm.name"
          placeholder="请输入企业名称"
          style="width: 200px"
        />
      </a-form-item>
      <a-form-item label="状态：">
        <a-select
          v-model="searchForm.status"
          placeholder="请选择"
          style="width: 150px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option value=""> 全部状态 </a-select-option>
          <a-select-option
            v-for="item in enterpriseStatusList"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" style="margin-top: 3px" icon="search" @click="search">
          查询
        </a-button>
        <a-button style="margin-left: 15px" icon="redo" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>
    <div style="margin-top: 15px">
      <div class="toolbar">
        <a-button type="primary" icon="plus" @click="handleAdd"> 新增 </a-button>
        <a-button
          style="margin-left: 12px"
          icon="qrcode"
          :loading="batchQrLoading"
          @click="handleBatchQr"
        >
          批量下载二维码
        </a-button>
      </div>
      <a-table
        :rowKey="(record) => record.id"
        :columns="columns"
        :data-source="dataInfo"
        :loading="listLoading"
        :pagination="pagination.total ? pagination : false"
        :scroll="{ x: 1565 }"
        bordered
        @change="pageChange"
      >
        <template slot="logo" slot-scope="text, record">
          <img
            v-if="record.logoUrl"
            :src="record.logoUrl"
            alt="logo"
            class="logo-img"
            @click="previewUrl = record.logoUrl; previewVisible = true"
          />
          <span v-else>--</span>
        </template>
        <span slot="status" slot-scope="text, record">
          <a-tag :color="record.status === EnterpriseStatusEnum.ON_SHELF ? 'green' : 'red'">
            {{ findLabel(enterpriseStatusList, record.status) || "--" }}
          </a-tag>
        </span>
        <span slot="action" slot-scope="text, record" class="action">
          <a-button type="link" class="btn" @click="handleView(record)"> 查看 </a-button>
          <a-button type="link" class="btn" @click="handleEdit(record)"> 编辑 </a-button>
          <a-button
            v-if="record.status === EnterpriseStatusEnum.ON_SHELF"
            type="link"
            class="btn"
            @click="handleOffShelf(record)"
          >
            下架
          </a-button>
          <a-button v-else type="link" class="btn" @click="handleOnShelf(record)"> 上架 </a-button>
          <a-button
            type="link"
            class="btn"
            :loading="qrLoadingId === record.id"
            @click="handleDownloadQr(record)"
          >
            下载二维码
          </a-button>
          <a-button type="link" class="btn" @click="handleIntention(record)">
            合作意向登记
          </a-button>
          <a-button type="link" class="btn btn-danger" @click="handleDelete(record)"> 删除 </a-button>
        </span>
      </a-table>
    </div>
    <!-- 新增/编辑弹窗 -->
    <a-modal
      :title="operationType === 'add' ? '新增企业' : '编辑企业'"
      :visible="visibleModal"
      :confirm-loading="confirmLoading"
      ok-text="保存"
      cancel-text="取消"
      width="800px"
      :maskClosable="false"
      @ok="handleOk"
      @cancel="visibleModal = false"
    >
      <a-form-model
        ref="ruleForm"
        :model="form"
        :rules="rules"
        :label-col="{ span: 4 }"
        :wrapper-col="{ span: 18 }"
      >
        <a-form-model-item label="企业名称" prop="name">
          <a-input
            v-model.trim="form.name"
            placeholder="请输入企业名称"
            :maxLength="20"
          />
        </a-form-model-item>
        <a-form-model-item label="企业logo" prop="logoFileList">
          <a-upload
            name="file"
            list-type="picture-card"
            class="avatar-uploader"
            :action="IMG_API + '/oss/files'"
            :file-list="form.logoFileList"
            :before-upload="logoBeforeUpload"
            @change="handleLogoChange"
            @preview="handlePreview"
          >
            <div v-if="form.logoFileList.length < 1">
              <a-icon type="plus" />
              <div class="ant-upload-text">上传logo</div>
            </div>
          </a-upload>
          <div class="upload-tip">须为 1:1 正方形，支持 jpg/png 格式，大小不限</div>
        </a-form-model-item>
        <a-form-model-item label="地点" prop="locationId">
          <a-select
            v-model="form.locationId"
            placeholder="请选择地点"
            allowClear
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          >
            <a-select-option v-for="item in locationOptions" :key="item.id" :value="item.id">
              {{ item.name }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item label="企业简介" prop="introduction">
          <a-textarea
            v-model.trim="form.introduction"
            placeholder="请输入企业简介"
            :rows="3"
            :maxLength="200"
          />
        </a-form-model-item>
        <a-form-model-item label="企业信息" prop="content">
          <div id="enterpriseEditor"></div>
        </a-form-model-item>
        <a-form-model-item label="排序" prop="sort" extra="数值越小越靠前，默认 0">
          <a-input-number
            v-model="form.sort"
            :min="0"
            :precision="0"
            placeholder="请输入排序"
            style="width: 200px"
          />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
    <!-- 查看弹窗 -->
    <a-modal
      title="企业详情"
      :visible="viewVisible"
      :footer="null"
      width="720px"
      @cancel="viewVisible = false"
    >
      <div class="view-item">
        <span class="view-label">企业logo：</span>
        <img v-if="viewData.logoUrl" :src="viewData.logoUrl" class="logo-img" alt="logo" />
        <span v-else>--</span>
      </div>
      <div class="view-item">
        <span class="view-label">企业名称：</span>{{ viewData.name || "--" }}
      </div>
      <div class="view-item">
        <span class="view-label">地点：</span>{{ viewData.locationName || "--" }}
      </div>
      <div class="view-item">
        <span class="view-label">企业简介：</span>{{ viewData.introduction || "--" }}
      </div>
      <div class="view-item">
        <span class="view-label">排序：</span>{{ viewData.sort }}
      </div>
      <div class="view-item">
        <span class="view-label">状态：</span>
        {{ findLabel(enterpriseStatusList, viewData.status) || "--" }}
      </div>
      <div class="view-item">
        <span class="view-label">创建时间：</span>{{ viewData.createTime || "--" }}
      </div>
      <div class="view-item view-content">
        <span class="view-label">企业信息：</span>
        <div class="rich-content" v-html="viewData.content"></div>
      </div>
    </a-modal>
    <!-- 合作意向登记弹窗（7.8 按企业分页查询，7.9 导出） -->
    <a-modal
      :title="`合作意向登记 - ${intentionRecord.name || ''}`"
      :visible="intentionVisible"
      :footer="null"
      width="960px"
      @cancel="intentionVisible = false"
    >
      <div class="intention-toolbar">
        <a-button
          type="primary"
          icon="download"
          :loading="intentionExportLoading"
          @click="handleExportIntentions"
        >
          导出
        </a-button>
      </div>
      <a-table
        :rowKey="(record) => record.id"
        :columns="intentionColumns"
        :data-source="intentionList"
        :loading="intentionLoading"
        :pagination="intentionPagination.total ? intentionPagination : false"
        :scroll="{ x: 920 }"
        size="small"
        bordered
        @change="intentionPageChange"
      >
      </a-table>
    </a-modal>
    <!-- logo 预览 -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false">
      <img :src="previewUrl" alt="logo" style="width: 100%" />
    </a-modal>
  </div>
</template>

<style scoped lang="scss">
.toolbar {
  margin-bottom: 10px;
}

.intention-toolbar {
  margin-bottom: 10px;
  text-align: right;
}

.logo-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
}

.action {
  // 操作按钮不允许换行，避免窄列下串行；按钮间留间距
  white-space: nowrap;

  .btn + .btn {
    margin-left: 12px;
  }
}

.btn {
  padding: 0;
}

.btn-danger {
  color: #f5222d;

  &:hover {
    color: #ff7875;
  }
}

.upload-tip {
  color: #999;
  font-size: 12px;
}

.view-item {
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;

  .view-label {
    flex-shrink: 0;
    color: #999;
  }
}

.rich-content {
  flex: 1;
  word-break: break-word;

  ::v-deep img {
    max-width: 100%;
  }
}
</style>
